import { MessagesController } from '@/api/messages'

export const messages = {
  state: {
    // MessagesController instance
    api: {},
    // Message's languages
    languages: window.LANGUAGES.sort((a, b) => a.name.localeCompare(b.name)),
    // Message's supported language pairs
    languagePairs: window.LANGUAGE_PAIRS,

    // Messages' agent representation
    agent: {},
    // Current thread
    thread: null,
    // Messages' first message representation
    firstMessage: null,
    // Messages' last message representation
    lastTranslation: null,
    // If we've requested the last translation
    lastTranslationRequested: false
  },
  actions: {
    async willAddNote ({ getters: { clientController, settings, authorized, validLanguagePair }, dispatch }) {
      if (settings.add_trigger_word && authorized && validLanguagePair) {
        clientController.prependTextToNote(`${settings.trigger_word}</br></br>`)
      }
    },

    async initializeMessages ({ getters, dispatch }) {
      console.log('will initialize api')
      await dispatch('initializeAPI')
      dispatch('showLoading', { message: 'Fetching Agent details' })
      console.log('will fetch agent')
      await dispatch('fetchAgent')
      if (getters.authorized) {
        dispatch('showLoading', { message: 'Fetching data from Unbabel' })
        console.log('Will fetch languages')
        await Promise.all([
          // dispatch('fetchLanguages'),
          // dispatch('fetchLanguagePairs'),
          dispatch('threadForMessage')
        ])

        console.log('will process thread')
        await dispatch('processThread')
      } else {
        const { code } = getters.agent.code
        if (code) {
          console.log('The agent with code:%s is not authorized', code)
        } else {
          console.log('The agent object for the code:%s was not created', code)
        }
        await dispatch('handleUnauthorizedAgent')
      }
      dispatch('hideLoading')
    },

    async signInWithApiKeyAction ({ dispatch, commit, getters: { api } }, unbabelApiKey) {
      const systemCode = await dispatch('fetchSystemCode')
      const organizationCode = await dispatch('fetchOrganizationCode')
      const { token, organizationId } = await api.signInWithApiKey(unbabelApiKey, organizationCode, systemCode)
      await dispatch('uploadAuthorization', { token, organizationId })
    },

    async initializeAPI ({ dispatch, commit, getters }) {
      const after = (organizationId = null, token = null) => {
        const controller = new MessagesController(organizationId, token)
        controller._fetch = getters.clientController.generateFetch(getters.clientController)
        commit('receivedAPI', controller)
      }
      try {
        const { organizationId, token } = await getters.clientController.fetchCredentials()
        after(organizationId, token)
      } catch (err) {
        after()
        throw err
      }
    },

    async fetchAgent ({ dispatch, commit, state: { api }, getters: { settings } }) {
      const { agentCode } = await dispatch('fetchClientAgent')
      const agent = await api.fetchAgent(agentCode)
      if (!agent) {
        console.log('Will not create agent since new agents are not authorized by default')
        return
      } else {
        console.log('The agent with the code:%s exists', agentCode)
      }
      commit('receivedAgent', agent)
      commit('receivedOrganizationId', agent.organization.id)
    },

    async processThread ({ state, getters, dispatch, commit }) {
      if (!state.thread && !getters.startAutomatically) {
        console.log('No thread and wont start automatically')
        commit('receivedLastTranslation', null)
        return
      }

      if (getters.customerLanguageCode && !getters.validLanguagePair) {
        console.log(`Won't translate because theres an invalid language pair ${getters.customerLanguageCode} <> ${getters.agentLanguageCode}`)
        return
      }

      const needsTranslation = await dispatch('needsTranslation')
      if (!needsTranslation) {
        console.log(`Will not translate because it doesn't need translation (there's already translations in this language pair)`)
        await dispatch('fetchLastTranslation')
        return
      }

      dispatch('showLoading', { message: 'Requesting translation' })
      await dispatch('translateFirstMessage')
      dispatch('hideLoading')
    },

    async fetchLastTranslation ({ state: { api }, getters, commit }) {
      if (!getters.thread) {
        commit('receivedLastTranslation', null)
        return
      }

      const { id } = getters.thread
      const translation = await api.fetchLastTranslationForThread(id)
      commit('receivedLastTranslation', translation)
    },

    async needsTranslation ({ state: { api, thread }, getters }) {
      if (getters.speaksCustomerLanguage) {
        console.log('Agent speaks the language')
        return false
      }

      if (!getters.thread) {
        console.log('Theres no thread object')
        return false
      }

      const hasTranslationsForLP = await api.threadHasTranslationsForLP(
        thread.id,
        getters.customerLanguageCode,
        getters.agentLanguageCode
      )

      return !hasTranslationsForLP
    },

    async translateFirstMessage ({ state, getters, commit }) {
      const { agentLanguageCode } = getters
      const { firstMessage: { id } } = state
      const translation = await state.api.requestTranslation(id, agentLanguageCode)
      commit('receivedLastTranslation', translation)
    },

    async threadForMessage ({ state: { api }, commit, getters, dispatch }, forceStart = false) {
      let result = null
      const payload = await dispatch('fetchPayloadForFirstMessage')
      console.log('start automatically %s', getters.startAutomatically)
      if (forceStart || getters.startAutomatically) {
        console.log('Will start automatically')
        result = await api.threadForFirstMessage(payload)
      } else {
        console.log(`Won't start automatically`)
        result = await api.fetchThreadAndFirstMessage(payload)
      }

      console.log('result', result)
      let { thread, message } = result
      if (message && !message.language) {
        message = await api.detectLanguage(message.id)
      }
      console.log('will receive')
      commit('receivedThread', thread)
      commit('receivedFirstMessage', message)
    }

  },

  getters: {
    lastTranslation: ({ lastTranslation }) => lastTranslation,
    lastTranslationRequested: ({ lastTranslationRequested }) => lastTranslationRequested,
    api: ({ api }) => api,
    agent: ({ agent }) => agent || {},
    thread: ({ thread }) => thread,
    settings: (state, { organization }) => organization.settings || {},
    languages: ({ languages }) => languages,
    organization: ({ agent }) => agent.organization || {},
    firstMessage: ({ firstMessage }) => firstMessage,
    languagePairs: ({ languagePairs }) => languagePairs,

    languageByCode: ({ languages }) => {
      return (languageCode) => languages.find(({ code }) => code === languageCode)
    },

    languagePairByCodes: (state, getters) => {
      return (source, target) => {
        return getters.languagePairs
          .find((lp) => lp.source.code === source && lp.target.code === target)
      }
    },

    agentLanguageCode: ({ agent }, { settings }) => agent.preferred_language || settings.fallback_language,
    customerLanguageCode: ({ firstMessage }) => (firstMessage || {}).language,

    agentLanguageName: ({ agent }, { settings, languageByCode }) => {
      return languageByCode(agent.preferred_language || settings.fallback_language).name
    },
    customerLanguageName: ({ firstMessage }, { languageByCode }) => {
      return languageByCode((firstMessage || {}).language).name
    },

    validLanguagePair: (state, { agentLanguageCode, customerLanguageCode, languagePairByCodes }) => {
      const lp1 = languagePairByCodes(agentLanguageCode, customerLanguageCode)
      const lp2 = languagePairByCodes(customerLanguageCode, agentLanguageCode)
      return lp1 && lp2 && lp1.active && lp2.active
    },

    sameLanguagePair: (state, { agentLanguageCode, customerLanguageCode }) => agentLanguageCode === customerLanguageCode,

    languagesSpoken: ({ agent: { languages_spoken: spoken } }) => spoken,
    speaksCustomerLanguage: (state, { customerLanguageCode, languagesSpoken, settings }) =>
      new Set(languagesSpoken.length ? languagesSpoken : [settings.fallback_language]).has(customerLanguageCode),

    authorized: ({ agent: { authorized } }) => authorized,
    translationMode: (state, { organization }) => organization.translation_mode,
    startAutomatically: (state, { settings }) => (settings || {}).start_automatically,

    translationModesAvailable: (state, { organization }) => ([].filter((x) => x)),

    lastTranslationIsWaiting: (state, { lastTranslation }) => lastTranslation && !lastTranslation.delivered,
    threadTranslationMode: ({ thread }) => thread.translation_mode
  },
  mutations: {
    receivedSettings (state, settings) {
      state.agent.organization.settings = settings
    },
    receivedLanguages (state, languages) {
      state.languages = languages
    },
    receivedLanguagePairs (state, languagePairs) {
      state.languagePairs = languagePairs
    },
    receivedThread (state, thread) {
      state.thread = thread
    },
    receivedFirstMessage (state, firstMessage) {
      state.firstMessage = firstMessage
    },
    receivedAPI (state, api) {
      state.api = api
    },
    receivedAgent (state, agent) {
      if (state.agent && state.agent.organization && state.agent.organization.settings && !agent.organization.settings) {
        agent.organization = state.agent.organization
      }
      state.agent = agent
    },
    receivedLastTranslation (state, translation) {
      state.lastTranslation = translation
      state.lastTranslationRequested = true
    },
    receivedOrganizationId (state, organizationId) {
      state.api.organizationId = organizationId
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/messages.js